import { useState, useEffect } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { MusicNote, Eye, EyeSlash, ArrowLeft, X, Hash } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AuthPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login, register } = useAuth();
  
  const [mode, setMode] = useState(searchParams.get('mode') === 'register' ? 'register' : 'login');
  const [userType, setUserType] = useState(searchParams.get('type') || 'musician');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [genres, setGenres] = useState([]);
  const [genreCategories, setGenreCategories] = useState([]);
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    genres: [], // Up to 3 for musicians
    genre_hashtags: [], // Up to 5 for organizers
    bio: '',
    location: '',
    website: '',
    spotify_url: '',
    soundcloud_url: '',
  });

  useEffect(() => {
    fetchGenres();
  }, []);

  const fetchGenres = async () => {
    try {
      const [genresRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/genres`),
        axios.get(`${API}/genre-categories`),
      ]);
      setGenres(genresRes.data);
      setGenreCategories(categoriesRes.data);
    } catch (error) {
      console.error('Failed to fetch genres:', error);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const toggleGenre = (genre) => {
    setFormData(prev => {
      const current = prev.genres || [];
      if (current.includes(genre)) {
        return { ...prev, genres: current.filter(g => g !== genre) };
      } else if (current.length < 3) {
        return { ...prev, genres: [...current, genre] };
      }
      return prev;
    });
  };

  const toggleHashtag = (category) => {
    setFormData(prev => {
      const current = prev.genre_hashtags || [];
      const hashtag = `#${category}`;
      if (current.includes(hashtag)) {
        return { ...prev, genre_hashtags: current.filter(h => h !== hashtag) };
      } else if (current.length < 5) {
        return { ...prev, genre_hashtags: [...current, hashtag] };
      }
      return prev;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (mode === 'login') {
        const user = await login(formData.email, formData.password);
        toast.success('Bentornato!');
        navigate(user.user_type === 'musician' ? '/dashboard' : '/organizer');
      } else {
        // Validate genres for musicians
        if (userType === 'musician' && formData.genres.length === 0) {
          toast.error('Seleziona almeno un genere musicale');
          setLoading(false);
          return;
        }
        
        const user = await register({
          ...formData,
          user_type: userType,
        });
        toast.success('Registrazione completata!');
        navigate(user.user_type === 'musician' ? '/dashboard' : '/organizer');
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Si è verificato un errore';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // Group genres by category for better display
  const genreGroups = {
    'Rock & Derivati': genres.filter(g => ['Rock', 'Hard Rock', 'Alternative', 'Indie', 'Grunge'].includes(g)),
    'Metal': genres.filter(g => ['Metal', 'Death Metal', 'Thrash Metal', 'Trash-core', 'Grind-core'].includes(g)),
    'Punk': genres.filter(g => ['Punk', 'Punk HC', 'Crust', 'Tekno-punk'].includes(g)),
    'Elettronica': genres.filter(g => ['Electronic', 'House', 'Techno', 'Tekno', 'Frenchcore', 'Drum & Bass', 'Dubstep', 'Trance'].includes(g)),
    'Classici': genres.filter(g => ['Pop', 'Hip Hop', 'R&B', 'Jazz', 'Blues', 'Soul', 'Funk'].includes(g)),
    'Altri': genres.filter(g => ['Reggae', 'Ska', 'Country', 'Folk', 'Classical', 'Latin', 'World', 'Experimental'].includes(g)),
  };

  return (
    <div className="min-h-screen bg-[#050505] flex">
      {/* Left Panel - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <img
          src="https://images.pexels.com/photos/5749200/pexels-photo-5749200.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
          alt="Music studio"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/50 to-transparent" />
        <div className="absolute bottom-12 left-12 max-w-md">
          <h2 className="text-3xl font-bold text-white mb-4">
            {mode === 'login' 
              ? 'Bentornato su BeatPromoter' 
              : 'Unisciti alla community'}
          </h2>
          <p className="text-[#A1A1AA]">
            {mode === 'login'
              ? 'Accedi per continuare a promuovere la tua musica'
              : 'Inizia a promuovere la tua musica o scopri nuovi talenti'}
          </p>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 overflow-y-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 mb-8 group">
            <ArrowLeft size={20} className="text-[#A1A1AA] group-hover:text-white transition-colors" />
            <div className="w-10 h-10 rounded-lg bg-[#CCFF00] flex items-center justify-center">
              <MusicNote size={24} weight="fill" className="text-black" />
            </div>
            <span className="font-bold text-xl">BeatPromoter</span>
          </Link>

          {/* Title */}
          <h1 className="text-2xl font-bold text-white mb-2">
            {mode === 'login' ? 'Accedi' : 'Crea un account'}
          </h1>
          <p className="text-[#A1A1AA] mb-8">
            {mode === 'login' 
              ? 'Inserisci le tue credenziali per accedere'
              : 'Compila il form per registrarti'}
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            {mode === 'register' && (
              <>
                {/* User Type Toggle */}
                <div className="flex gap-2 p-1 bg-[#111] rounded-lg">
                  <button
                    type="button"
                    onClick={() => setUserType('musician')}
                    className={`flex-1 py-2.5 px-4 rounded-md text-sm font-medium transition-all ${
                      userType === 'musician'
                        ? 'bg-[#CCFF00] text-black'
                        : 'text-[#A1A1AA] hover:text-white'
                    }`}
                    data-testid="auth-type-musician"
                  >
                    Musicista
                  </button>
                  <button
                    type="button"
                    onClick={() => setUserType('organizer')}
                    className={`flex-1 py-2.5 px-4 rounded-md text-sm font-medium transition-all ${
                      userType === 'organizer'
                        ? 'bg-[#CCFF00] text-black'
                        : 'text-[#A1A1AA] hover:text-white'
                    }`}
                    data-testid="auth-type-organizer"
                  >
                    Organizzatore
                  </button>
                </div>

                {/* Name */}
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-white">
                    {userType === 'musician' ? 'Nome artista / Band' : 'Nome locale / Azienda'}
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder={userType === 'musician' ? 'Es. The Rockets' : 'Es. Club XYZ'}
                    required
                    className="bg-[#111] border-white/10 text-white placeholder:text-[#666] focus:border-[#CCFF00] focus:ring-[#CCFF00]/20"
                    data-testid="auth-name-input"
                  />
                </div>
              </>
            )}

            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-white">Email</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="nome@esempio.com"
                required
                className="bg-[#111] border-white/10 text-white placeholder:text-[#666] focus:border-[#CCFF00] focus:ring-[#CCFF00]/20"
                data-testid="auth-email-input"
              />
            </div>

            {/* Password */}
            <div className="space-y-2">
              <Label htmlFor="password" className="text-white">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="••••••••"
                  required
                  minLength={6}
                  className="bg-[#111] border-white/10 text-white placeholder:text-[#666] focus:border-[#CCFF00] focus:ring-[#CCFF00]/20 pr-10"
                  data-testid="auth-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A1A1AA] hover:text-white"
                >
                  {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {/* Musician: Multi-genre selection */}
            {mode === 'register' && userType === 'musician' && (
              <>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-white">Generi musicali (max 3)</Label>
                    <span className="text-xs text-[#A1A1AA]">{formData.genres.length}/3 selezionati</span>
                  </div>
                  
                  {/* Selected genres */}
                  {formData.genres.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-2">
                      {formData.genres.map(genre => (
                        <span
                          key={genre}
                          className="inline-flex items-center gap-1 px-3 py-1 bg-[#CCFF00] text-black text-sm rounded-full font-medium"
                        >
                          {genre}
                          <button type="button" onClick={() => toggleGenre(genre)}>
                            <X size={14} />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  
                  {/* Genre groups */}
                  <div className="max-h-64 overflow-y-auto space-y-4 p-3 bg-[#0A0A0A] rounded-lg border border-white/10">
                    {Object.entries(genreGroups).map(([groupName, groupGenres]) => (
                      groupGenres.length > 0 && (
                        <div key={groupName}>
                          <p className="text-xs text-[#666] uppercase tracking-wider mb-2">{groupName}</p>
                          <div className="flex flex-wrap gap-2">
                            {groupGenres.map(genre => (
                              <button
                                key={genre}
                                type="button"
                                onClick={() => toggleGenre(genre)}
                                disabled={!formData.genres.includes(genre) && formData.genres.length >= 3}
                                className={`px-3 py-1 text-sm rounded-full border transition-all ${
                                  formData.genres.includes(genre)
                                    ? 'bg-[#CCFF00] text-black border-[#CCFF00]'
                                    : formData.genres.length >= 3
                                    ? 'bg-transparent text-[#666] border-[#333] cursor-not-allowed'
                                    : 'bg-transparent text-white border-white/20 hover:border-[#CCFF00]'
                                }`}
                                data-testid={`genre-${genre}`}
                              >
                                {genre}
                              </button>
                            ))}
                          </div>
                        </div>
                      )
                    ))}
                  </div>
                </div>

                {/* Location */}
                <div className="space-y-2">
                  <Label htmlFor="location" className="text-white">Città</Label>
                  <Input
                    id="location"
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    placeholder="Es. Milano"
                    className="bg-[#111] border-white/10 text-white placeholder:text-[#666] focus:border-[#CCFF00] focus:ring-[#CCFF00]/20"
                    data-testid="auth-location-input"
                  />
                </div>

                {/* Bio */}
                <div className="space-y-2">
                  <Label htmlFor="bio" className="text-white">Bio (opzionale)</Label>
                  <Textarea
                    id="bio"
                    name="bio"
                    value={formData.bio}
                    onChange={handleChange}
                    placeholder="Raccontaci di te e della tua musica..."
                    rows={3}
                    className="bg-[#111] border-white/10 text-white placeholder:text-[#666] focus:border-[#CCFF00] focus:ring-[#CCFF00]/20 resize-none"
                    data-testid="auth-bio-input"
                  />
                </div>
              </>
            )}

            {/* Organizer: Hashtag selection */}
            {mode === 'register' && userType === 'organizer' && (
              <>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-white">Generi che cerchi (max 5)</Label>
                    <span className="text-xs text-[#A1A1AA]">{formData.genre_hashtags.length}/5 selezionati</span>
                  </div>
                  <p className="text-xs text-[#666]">Seleziona le macro-categorie musicali che ti interessano</p>
                  
                  {/* Selected hashtags */}
                  {formData.genre_hashtags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-2">
                      {formData.genre_hashtags.map(hashtag => (
                        <span
                          key={hashtag}
                          className="inline-flex items-center gap-1 px-3 py-1 bg-[#FF0055] text-white text-sm rounded-full font-medium"
                        >
                          {hashtag}
                          <button type="button" onClick={() => toggleHashtag(hashtag.replace('#', ''))}>
                            <X size={14} />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  
                  {/* Hashtag grid */}
                  <div className="flex flex-wrap gap-2 p-3 bg-[#0A0A0A] rounded-lg border border-white/10">
                    {genreCategories.map(category => {
                      const hashtag = `#${category}`;
                      const isSelected = formData.genre_hashtags.includes(hashtag);
                      return (
                        <button
                          key={category}
                          type="button"
                          onClick={() => toggleHashtag(category)}
                          disabled={!isSelected && formData.genre_hashtags.length >= 5}
                          className={`px-3 py-2 text-sm rounded-full border transition-all flex items-center gap-1 ${
                            isSelected
                              ? 'bg-[#FF0055] text-white border-[#FF0055]'
                              : formData.genre_hashtags.length >= 5
                              ? 'bg-transparent text-[#666] border-[#333] cursor-not-allowed'
                              : 'bg-transparent text-white border-white/20 hover:border-[#FF0055]'
                          }`}
                          data-testid={`hashtag-${category}`}
                        >
                          <Hash size={14} />
                          {category}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="location" className="text-white">Città</Label>
                  <Input
                    id="location"
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    placeholder="Es. Roma"
                    className="bg-[#111] border-white/10 text-white placeholder:text-[#666] focus:border-[#CCFF00] focus:ring-[#CCFF00]/20"
                    data-testid="auth-location-input"
                  />
                </div>
              </>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#CCFF00] text-black hover:bg-[#E5FF00] rounded-full font-semibold h-12"
              data-testid="auth-submit-btn"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
              ) : mode === 'login' ? (
                'Accedi'
              ) : (
                'Registrati'
              )}
            </Button>
          </form>

          {/* Toggle Mode */}
          <p className="text-center text-[#A1A1AA] mt-6">
            {mode === 'login' ? (
              <>
                Non hai un account?{' '}
                <button
                  onClick={() => setMode('register')}
                  className="text-[#CCFF00] hover:underline font-medium"
                  data-testid="auth-toggle-mode"
                >
                  Registrati
                </button>
              </>
            ) : (
              <>
                Hai già un account?{' '}
                <button
                  onClick={() => setMode('login')}
                  className="text-[#CCFF00] hover:underline font-medium"
                  data-testid="auth-toggle-mode"
                >
                  Accedi
                </button>
              </>
            )}
          </p>
        </motion.div>
      </div>
    </div>
  );
}
